<template>
  <b-modal
    id="add-pickup-modal"
    ref="add-pickup-modal"
    cancel-variant="outline-secondary"
    :title="t('Call Courier')"
    :ok-title="t('Save')"
    :cancel-title="t('Close')"
    centered
    size="lg"
    @ok="handleOk"
  >
    <b-row>
      <b-col md="6" lg="6">
        <b-form-group label-for="call_courier_address_book">
          <div class="d-flex form-group-label">
            <label for="name">{{ t('Fill From Address Book') }}</label>
          </div>
          <v-select
            id="call_courier_address_book"
            v-model="callCourierAddressBook"
            :options="address_book_list"
            :placeholder="t('Make a Choice')"
            label="text"
            @input="setCurrentAddressData"
          >
            <span slot="no-options">{{ t('No records found') }}</span>
          </v-select>
        </b-form-group>
      </b-col>
      <div class="divider-field my-1"></div>
    </b-row>
    <validation-observer ref="callCourierForm" tag="form">
      <b-row>
        <b-col md="6" lg="6">
          <validation-provider
            #default="{ errors }"
            :name="t('Country')"
            rules="required"
            vid="call_courier_country"
          >
            <b-form-group
              label-for="call_courier_country"
              :state="errors.length > 0 ? false : null"
            >
              <div class="d-flex form-group-label">
                <label for="name">{{ t('Country') }}</label>
                <span class="text-danger">*</span>
              </div>
              <v-select
                id="call_courier_country"
                v-model="call_courier.addressCountry"
                :options="countries"
                :placeholder="t('Make a Choice')"
                label="text"
                @input="getStateList"
              >
                <span slot="no-options">{{ t('No records found') }}</span>
              </v-select>
              <b-form-invalid-feedback :state="errors.length > 0 ? false : null">
                {{ errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>
        </b-col>
        <b-col md="6" lg="6">
          <validation-provider
            #default="{ errors }"
            :name="t('Postal Code')"
            vid="call_courier_postal_code"
            ref="callCourierPostalCodeValidation"
            rules="required"
          >
            <b-form-group
              label-for="call_courier_postal_code"
              :state="errors.length > 0 ? false : null"
            >
              <div class="d-flex form-group-label">
                <label for="name">{{ t('Postal Code') }}</label>
                <span class="text-danger">*</span>
              </div>
              <v-select
                id="call_courier_postal_code"
                :disabled="!call_courier.addressCountry || !call_courier.addressCountry.value"
                v-model="call_courier.postal_code"
                :options="postalCodeOptions"
                :placeholder="t('Enter Postal Code')"
                label="combinedLabel"
                @search="handlePostalCodeSearch"
                @input="handleFromPostalCode"
              >
                <span slot="no-options">{{ t('No records found') }}</span>
              </v-select>
              <b-form-invalid-feedback :state="errors.length > 0 ? false : null">
                {{ errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>
        </b-col>
        <b-col md="6" lg="6">
          <validation-provider
            #default="{ errors }"
            ref="callCourierStateValidation"
            :name="t('City')"
            rules="required"
            vid="call_courier_state"
          >
            <b-form-group label-for="call_courier_state" :state="errors.length > 0 ? false : null">
              <div class="d-flex form-group-label">
                <label for="name">{{ t('City') }}</label>
                <span class="text-danger">*</span>
              </div>
              <v-select
                id="call_courier_state"
                v-model="call_courier.addressState"
                :options="consignorStates"
                :placeholder="t('Make a Choice')"
                label="text"
                @input="getCityList"
              >
                <span slot="no-options">{{ t('No records found') }}</span>
              </v-select>
              <b-form-invalid-feedback :state="errors.length > 0 ? false : null">
                {{ errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>
        </b-col>
        <b-col md="6" lg="6">
          <validation-provider
            #default="{ errors }"
            ref="callCourierCityValidation"
            :name="t('District')"
            rules="required"
            vid="call_courier_city"
          >
            <b-form-group label-for="call_courier_city" :state="errors.length > 0 ? false : null">
              <div class="d-flex form-group-label">
                <label for="name">{{ t('District') }}</label>
                <span class="text-danger">*</span>
              </div>
              <v-select
                id="call_courier_city"
                v-model="call_courier.addressCity"
                :options="consignorCities"
                :placeholder="t('Make a Choice')"
                label="text"
                @input="resetAddressBook"
              >
                <span slot="no-options">{{ t('No records found') }}</span>
              </v-select>
              <b-form-invalid-feedback :state="errors.length > 0 ? false : null">
                {{ errors[0] }}
              </b-form-invalid-feedback>
            </b-form-group>
          </validation-provider>
        </b-col>
        <b-col md="6" lg="6">
          <b-form-group label-for="call_courier_name">
            <div class="d-flex form-group-label">
              <label for="name">{{ t('Address Name') }}</label>
              <span class="text-danger">*</span>
            </div>
            <validation-provider
              #default="{ errors }"
              :name="t('Address Name')"
              vid="call_courier_name"
            >
              <b-form-input
                v-model="call_courier.addressName"
                :state="errors.length > 0 ? false : null"
                :placeholder="t('Address Name')"
                tabindex="4"
                @change="resetAddressBook"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>
        </b-col>
        <b-col md="6" lg="6">
          <b-form-group label-for="call_courier_address">
            <div class="d-flex form-group-label">
              <label for="name">{{ t('Address') }}</label>
              <span class="text-danger">*</span>
            </div>
            <validation-provider
              #default="{ errors }"
              :name="t('Address')"
              rules="required"
              vid="call_courier_address"
            >
              <b-form-input
                v-model="call_courier.address"
                :state="errors.length > 0 ? false : null"
                :placeholder="t('Address')"
                tabindex="4"
                @change="resetAddressBook"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>
        </b-col>
        <b-col md="12" lg="6">
          <b-form-group label-for="appointmentStart">
            <div class="d-flex form-group-label">
              <label for="name">{{ t('Appointment Start') }}</label>
              <span class="text-danger">*</span>
            </div>
            <validation-provider
              #default="{ errors }"
              :name="t('Appointment Start')"
              rules="required"
              vid="appointmentStart"
            >
              <flat-pickr
                v-model="call_courier.appointmentStart"
                class="form-control"
                name="appointmentStart"
                :placeholder="t('Select Date')"
                :config="{
                  enableTime: true,
                  dateFormat: 'd-m-Y H:i',
                  time_24hr: true,
                  minDate: 'today'
                }"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>
        </b-col>
        <b-col md="12" lg="6">
          <b-form-group label-for="appointmentEnd">
            <div class="d-flex form-group-label">
              <label for="name">{{ t('Appointment End') }}</label>
              <span class="text-danger">*</span>
            </div>
            <validation-provider
              #default="{ errors }"
              :name="t('Appointment End')"
              rules="required"
              vid="appointmentEnd"
            >
              <flat-pickr
                v-model="call_courier.appointmentEnd"
                class="form-control"
                name="appointmentEnd"
                :placeholder="t('Select Date')"
                :config="{
                  enableTime: true,
                  dateFormat: 'd-m-Y H:i',
                  time_24hr: true,
                  minDate: call_courier.appointmentStart
                }"
              />
              <small class="text-danger">{{ errors[0] }}</small>
            </validation-provider>
          </b-form-group>
        </b-col>
      </b-row>
    </validation-observer>
  </b-modal>
</template>

<script>
import { BFormGroup, BFormInput, BRow, BCol, BFormInvalidFeedback } from 'bootstrap-vue';
import { helper } from '@core/mixins/helper.js';
import { mapGetters } from 'vuex';
import flatPickr from 'vue-flatpickr-component';
import { useUtils as useI18nUtils } from '@core/libs/i18n';
import useService from '@/auth/service/useService';
import vSelect from 'vue-select';
import { ValidationProvider, ValidationObserver } from 'vee-validate';
import debounce from 'lodash.debounce';

export default {
  components: {
    BFormGroup,
    BFormInput,
    BRow,
    BCol,
    ValidationProvider,
    ValidationObserver,
    vSelect,
    BFormInvalidFeedback,

    flatPickr
  },
  mixins: [helper],
  props: {
    address_book_list: Array,
    selected: Array
  },

  computed: {
    ...mapGetters({
      countries: 'app/countries',
      consignorStates: 'app/consignorStates',
      consignorCities: 'app/consignorCities'
    }),
    postalCodeOptions() {
      return this.filteredPostalCodes.map(code => ({
        value: code.zipcode,
        combinedLabel: `${code.zipcode} - ${code.place}`,
        state: code.state,
        city: code.city
      }));
    }
  },
  data() {
    return {
      callCourierAddressBook: '',
      call_courier: {
        addressCountry: '',
        addressState: '',
        addressCity: '',
        addressName: '',
        address: '',
        appointmentStart: '',
        appointmentEnd: '',
        postal_code: ''
      },
      editedId: '',
      filteredPostalCodes: [],
      minimumPostalCodeLength: 3
    };
  },
  methods: {
    handleOk(e) {
      e.preventDefault();

      const requestData = {
        appointment: {
          start: `${this.call_courier.appointmentStart}:00`,
          end: `${this.call_courier.appointmentEnd}:59`
        },
        address: {
          name: this.call_courier.addressName,
          country_id: this.call_courier.addressCountry.value || '',
          state_id: this.call_courier.addressState.value || '',
          city_id: this.call_courier.addressCity.value || '',
          street: this.call_courier.address,
          postal_code:
            this.call_courier.postal_code && typeof this.call_courier.postal_code === 'object'
              ? this.call_courier.postal_code.value || this.call_courier.postal_code.combinedLabel
              : this.call_courier.postal_code || ''
        }
      };

      if (!this.editedId) {
        requestData.consignments = this.selected
          .filter(item => item.type === 'barcode')
          .map(item => ({
            type: item.type,
            value: item.value
          }));
      }

      this.$refs.callCourierForm.validate().then(success => {
        if (success) {
          if (this.editedId) {
            useService.pickupService
              .updatePickup(requestData, this.editedId)
              .then(response => {
                this.showSuccessMessage(response.message);
                this.$emit('triggerList');
                this.$bvModal.hide('add-pickup-modal');
              })
              .catch(messages => {
                if (typeof messages === 'object' && messages && messages.length) {
                  messages.forEach(item => {
                    this.showErrorMessage(item.message);
                  });
                }
              });
          } else {
            useService.pickupService
              .createPickup(requestData)
              .then(response => {
                this.showSuccessMessage(response.message);
                this.$router.push({ name: 'pickups' });
                this.selected = [];
              })
              .catch(messages => {
                if (typeof messages === 'object' && messages && messages.length) {
                  messages.forEach(item => {
                    this.showErrorMessage(item.message);
                  });
                }
              });
          }
        }
      });
    },
    getStateList(data) {
      this.call_courier.addressState = '';
      this.$refs.callCourierStateValidation.reset();

      this.call_courier.postal_code = '';
      if (this.$refs.callCourierPostalCodeValidation) {
        this.$refs.callCourierPostalCodeValidation.reset();
      }

      this.resetAddressBook();

      this.getCityList('');

      this.filteredPostalCodes = [];

      if (data && data.value) {
        this.$store.dispatch('app/getConsignorStates', data.value);
      }
    },
    async getCityList(data) {
      this.call_courier.addressCity = '';
      this.$refs.callCourierCityValidation?.reset();

      this.resetAddressBook();

      if (data && data.value) {
        await this.$store.dispatch('app/getConsignorCities', {
          countryId: this.call_courier.addressCountry?.value,
          cityId: data.value
        });
      }
    },
    resetAddressBook() {
      this.callCourierAddressBook = '';
    },
    async setCurrentAddressData(data) {
      if (!data) {
        this.call_courier.addressCountry = '';
        this.call_courier.addressState = '';
        this.call_courier.addressCity = '';
        this.call_courier.addressName = '';
        this.call_courier.address = '';
        this.call_courier.postal_code = '';
        this.filteredPostalCodes = [];
        return false;
      }

      if (data.country) {
        this.call_courier.addressCountry = this.countries.find(x => x.value === data.country.id);
      }

      if (data.state) {
        this.call_courier.addressState = {
          text: data.state.name,
          value: data.state.id,
          stateCode: data.state.stateCode
        };
      }

      if (data.city) {
        this.call_courier.addressCity = {
          text: data.city.name,
          value: data.city.id
        };
      }

      if (data.name) {
        this.call_courier.addressName = data.name;
      }

      if (data.address) {
        this.call_courier.address = data.address;
      }

      if (data.postalCode) {
        this.call_courier.postal_code = {
          value: data.postalCode.zipcode,
          combinedLabel: `${data.postalCode.zipcode} - ${data.postalCode.place}`
        };
      } else if (data.postal_code || data.zipcode) {
        this.call_courier.postal_code = data.postal_code || data.zipcode;
      }

      return data;
    },
    clearEditedId() {
      this.editedId = '';
    },
    setPickupAddressData(data) {
      if (!data) return false;

      this.editedId = data.id;

      if (data.country) {
        this.call_courier.addressCountry = this.countries.find(x => x.value === data.country.id);
        this.getStateList(this.call_courier.addressCountry);
      }

      if (data.state) {
        this.call_courier.addressState = {
          text: data.state.name,
          value: data.state.id,
          stateCode: data.state.stateCode
        };
        this.getCityList(this.call_courier.addressState);
      }

      if (data.city) {
        this.call_courier.addressCity = {
          text: data.city.name,
          value: data.city.id
        };
      }

      if (data.name) {
        this.call_courier.addressName = data.name;
      }

      if (data.address) {
        this.call_courier.address = data.address;
      }

      if (data.appointmentStart) {
        this.call_courier.appointmentStart = data.appointmentStart;
      }

      if (data.appointmentEnd) {
        this.call_courier.appointmentEnd = data.appointmentEnd;
      }

      if (data.postalCode) {
        this.call_courier.postal_code = {
          value: data.postalCode.zipcode,
          combinedLabel: `${data.postalCode.zipcode} - ${data.postalCode.place}`
        };
      } else if (data.postal_code || data.zipcode) {
        this.call_courier.postal_code = data.postal_code || data.zipcode;
      }
    },
    async handleFromPostalCode(selected) {
      if (!selected || typeof selected !== 'object') {
        this.call_courier.addressState = '';
        this.call_courier.addressCity = '';
        return;
      }

      if (!selected.state) return;

      const sanitize = t => {
        return (t || '')
          .toString()
          .replace(/İ/g, 'I')
          .replace(/i/g, 'I')
          .replace(/ı/g, 'I')
          .replace(/Ş/g, 'S')
          .replace(/ş/g, 'S')
          .replace(/Ğ/g, 'G')
          .replace(/ğ/g, 'G')
          .replace(/Ü/g, 'U')
          .replace(/ü/g, 'U')
          .replace(/Ö/g, 'O')
          .replace(/ö/g, 'O')
          .replace(/Ç/g, 'C')
          .replace(/ç/g, 'C')
          .toUpperCase()
          .trim();
      };

      const findItem = (list, text) => list.find(x => sanitize(x.text) === sanitize(text));

      const state = findItem(this.consignorStates, selected.state);

      if (state) {
        this.call_courier.addressState = state;
        await this.getCityList(state);
        this.call_courier.addressCity = findItem(this.consignorCities, selected.city) || '';
      }
    },
    handlePostalCodeSearch: debounce(async function searchPostalCode(zipcode) {
      const cc = this.call_courier.addressCountry?.iso2;
      if (!cc || zipcode?.length < this.minimumPostalCodeLength) return;

      const { data = [] } = await useService.autoPostalCodes({ country_code: cc, zipcode });
      this.filteredPostalCodes = data
        .filter(c => c.zipcode?.includes(zipcode))
        .map(c => ({
          place: c.place,
          zipcode: c.zipcode,
          state: c.state,
          city: c.province || c.city
        }));
    }, 400)
  },
  setup() {
    const { t } = useI18nUtils();

    return {
      t
    };
  }
};
</script>
<style lang="scss">
@import '@core/scss/vue/libs/vue-good-table.scss';
</style>
